import { useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

/**
 * Custom hook for managing Spotify app pool credentials.
 *
 * Resolves the correct client_id / client_secret for a user by calling
 * the /get-user-spotify-app endpoint.  Persists resolved credentials
 * and user email in localStorage so returning users skip the gate.
 */
export function useSpotifyCredentials() {
  const [resolvedClientId, setResolvedClientId] = useState(
    () => localStorage.getItem('resolved_client_id')
  );
  const [resolvedClientSecret, setResolvedClientSecret] = useState(
    () => localStorage.getItem('resolved_client_secret')
  );
  const [credentialsResolved, setCredentialsResolved] = useState(
    () => !!localStorage.getItem('resolved_client_id')
  );
  const [credentialsLoading, setCredentialsLoading] = useState(false);
  const [userNotRegistered, setUserNotRegistered] = useState(false);
  const [userEmail, setUserEmail] = useState(
    () => localStorage.getItem('user_email') || ''
  );

  /** Call the backend to resolve credentials for a given email. */
  const resolveCredentials = useCallback(async (email) => {
    setCredentialsLoading(true);
    setUserNotRegistered(false);
    try {
      const response = await axios.post(
        `${API_BASE_URL}/get-user-spotify-app`,
        { email }
      );
      const { client_id, client_secret } = response.data;

      // Persist to localStorage
      localStorage.setItem('resolved_client_id', client_id);
      localStorage.setItem('resolved_client_secret', client_secret);
      localStorage.setItem('user_email', email);

      setResolvedClientId(client_id);
      setResolvedClientSecret(client_secret);
      setCredentialsResolved(true);
      setUserEmail(email);
      setCredentialsLoading(false);
      return true;
    } catch (error) {
      console.error('[Auth] Failed to resolve Spotify credentials:', error);
      setCredentialsLoading(false);
      if (error.response?.status === 404) {
        setUserNotRegistered(true);
      } else {
        toast.error(
          'Failed to verify your account. Please try again.',
          { theme: 'colored', position: 'top-center' }
        );
      }
      return false;
    }
  }, []);

  /** Clear all credential state (call on logout). */
  const clearCredentials = useCallback(() => {
    localStorage.removeItem('resolved_client_id');
    localStorage.removeItem('resolved_client_secret');
    localStorage.removeItem('user_email');
    setResolvedClientId(null);
    setResolvedClientSecret(null);
    setCredentialsResolved(false);
  }, []);

  return {
    clientId: resolvedClientId,
    clientSecret: resolvedClientSecret,
    credentialsResolved,
    credentialsLoading,
    userNotRegistered,
    userEmail,
    setUserEmail,
    setUserNotRegistered,
    resolveCredentials,
    clearCredentials,
  };
}
